// src/pages/admin/ChallengesManage.js
import React, { useState, useEffect } from 'react';
import { Table, Button, Input, Space, Popconfirm, message, Typography, Modal, Form, Input as AntInput, Select, Tag, DatePicker } from 'antd';
import { PlusOutlined, EditOutlined, EyeOutlined, StopOutlined } from '@ant-design/icons';
import moment from 'moment'; // For handling dates with Ant Design's DatePicker

const { Title } = Typography;
const { Option } = Select;

const ChallengesManage = () => {
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [form] = Form.useForm();

  // Simulated API call to fetch challenges
  const fetchChallenges = async (page = 1, pageSize = 10, search = '') => {
    setLoading(true);
    try {
      const mockData = [
        {
          id: 1,
          title: 'AI Hackathon 2025',
          description: 'Build an AI-powered solution',
          category_id: 1, // Assume category_id 1 is 'AI'
          country: 'Kenya',
          organizer: 'JectHub',
          difficulty_level: 'INTERMEDIATE',
          deadline: '2025-06-30T23:59:59Z',
          days_left: Math.floor((new Date('2025-06-30') - new Date()) / (1000 * 60 * 60 * 24)), // Calculated
          prize: 500.00,
          is_open: true,
          participants_count: 42,
          submission_deadline: '2025-07-07T23:59:59Z',
          submission_count: 15,
          created_at: '2025-01-01T10:00:00Z',
          updated_at: '2025-03-01T12:00:00Z',
        },
        // Add more mock data as needed
      ].filter(challenge =>
        challenge.title.toLowerCase().includes(search.toLowerCase()) ||
        challenge.description.toLowerCase().includes(search.toLowerCase())
      );

      const start = (page - 1) * pageSize;
      const end = start + pageSize;
      const paginatedData = mockData.slice(start, end);

      setChallenges(paginatedData);
      setPagination({
        current: page,
        pageSize: pageSize,
        total: mockData.length,
      });
    } catch (error) {
      message.error('Failed to fetch challenges');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChallenges(pagination.current, pagination.pageSize, searchText);
  }, [pagination.current, pagination.pageSize, searchText]);

  const columns = [
    { title: 'Id', dataIndex: 'id', key: 'id', sorter: (a, b) => a.id - b.id },
    { title: 'Title', dataIndex: 'title', key: 'title', sorter: (a, b) => a.title.localeCompare(b.title) },
    { title: 'Description', dataIndex: 'description', key: 'description' },
    { title: 'Country', dataIndex: 'country', key: 'country' },
    { title: 'Difficulty', dataIndex: 'difficulty_level', key: 'difficulty_level' },
    { title: 'Deadline', dataIndex: 'deadline', key: 'deadline', render: date => moment(date).format('YYYY-MM-DD') },
    { title: 'Days Left', dataIndex: 'days_left', key: 'days_left' },
    { title: 'Prize ($)', dataIndex: 'prize', key: 'prize' },
    { title: 'Status', dataIndex: 'is_open', key: 'is_open', render: isOpen => (isOpen ? 'Open' : 'Closed'), filters: [
        { text: 'Open', value: true },
        { text: 'Closed', value: false },
      ], onFilter: (value, record) => record.is_open === value },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button icon={<EyeOutlined />} onClick={() => handleView(record)}>View</Button>
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)}>Edit</Button>
          {record.is_open && (
            <Popconfirm
              title="Are you sure to close this challenge?"
              onConfirm={() => handleClose(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button icon={<StopOutlined />} danger>Close</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const handleCreate = () => {
    setCreateModalVisible(true);
  };

  const handleView = (challenge) => {
    setSelectedChallenge(challenge);
    setViewModalVisible(true);
  };

  const handleEdit = (challenge) => {
    setSelectedChallenge(challenge);
    form.setFieldsValue({
      ...challenge,
      deadline: moment(challenge.deadline),
      submission_deadline: challenge.submission_deadline ? moment(challenge.submission_deadline) : null,
    }); // Pre-fill form with challenge data, converting dates to moment objects
    setEditModalVisible(true);
  };

  const handleClose = async (id) => {
    try {
      const updatedChallenges = challenges.map(challenge =>
        challenge.id === id ? { ...challenge, is_open: false, updated_at: new Date().toISOString() } : challenge
      );
      setChallenges(updatedChallenges);
      message.success('Challenge closed successfully');
      fetchChallenges(pagination.current, pagination.pageSize, searchText);
    } catch (error) {
      message.error('Failed to close challenge');
      console.error(error);
    }
  };

  const handleTableChange = (newPagination) => {
    fetchChallenges(newPagination.current, newPagination.pageSize, searchText);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination({ ...pagination, current: 1 });
  };

  const handleCreateSubmit = async (values) => {
    try {
      const newChallenge = {
        id: challenges.length + 1,
        ...values,
        deadline: values.deadline.toISOString(),
        submission_deadline: values.submission_deadline ? values.submission_deadline.toISOString() : null,
        days_left: Math.floor((values.deadline - new Date()) / (1000 * 60 * 60 * 24)), // Calculated
        participants_count: 0,
        submission_count: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        is_open: values.is_open !== undefined ? values.is_open : true, // Default to true
      };
      setChallenges([...challenges, newChallenge]);
      message.success('Challenge created successfully');
      setCreateModalVisible(false);
      form.resetFields();
      fetchChallenges(pagination.current, pagination.pageSize, searchText);
    } catch (error) {
      message.error('Failed to create challenge');
      console.error(error);
    }
  };

  const handleEditSubmit = async (values) => {
    try {
      const updatedChallenges = challenges.map(challenge =>
        challenge.id === selectedChallenge.id ? {
          ...challenge,
          ...values,
          deadline: values.deadline.toISOString(),
          submission_deadline: values.submission_deadline ? values.submission_deadline.toISOString() : null,
          days_left: Math.floor((values.deadline - new Date()) / (1000 * 60 * 60 * 24)),
          updated_at: new Date().toISOString(),
        } : challenge
      );
      setChallenges(updatedChallenges);
      message.success('Challenge updated successfully');
      setEditModalVisible(false);
      form.resetFields();
      fetchChallenges(pagination.current, pagination.pageSize, searchText);
    } catch (error) {
      message.error('Failed to update challenge');
      console.error(error);
    }
  };

  const challengeFormFields = (
    <>
      <Form.Item name="title" label="Challenge Title" rules={[{ required: true, message: 'Please enter the challenge title' }]}>
        <AntInput placeholder="e.g., AI Hackathon 2025" />
      </Form.Item>
      <Form.Item name="description" label="Description" rules={[{ required: true, message: 'Please enter a description' }]}>
        <AntInput.TextArea placeholder="e.g., Build an AI-powered solution" rows={4} />
      </Form.Item>
      <Form.Item name="category_id" label="Category ID" rules={[{ required: true, message: 'Please enter the category ID' }]}>
        <AntInput type="number" placeholder="e.g., 1 for AI" min={1} />
      </Form.Item>
      <Form.Item name="country" label="Country" rules={[{ required: true, message: 'Please enter the country' }]}>
        <AntInput placeholder="e.g., Kenya" />
      </Form.Item>
      <Form.Item name="organizer" label="Organizer">
        <AntInput placeholder="e.g., JectHub" />
      </Form.Item>
      <Form.Item name="difficulty_level" label="Difficulty Level" rules={[{ required: true, message: 'Please select the difficulty level' }]}>
        <Select placeholder="Select difficulty">
          <Option value="BEGINNER">Beginner</Option>
          <Option value="INTERMEDIATE">Intermediate</Option>
          <Option value="ADVANCED">Advanced</Option>
        </Select>
      </Form.Item>
      <Form.Item
        name="deadline"
        label="Deadline"
        rules={[
          { required: true, message: 'Please select the deadline' },
          { validator: (_, value) => value && value.isAfter(moment()) ? Promise.resolve() : Promise.reject('Deadline must be in the future') },
        ]}
      >
        <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" />
      </Form.Item>
      <Form.Item
        name="submission_deadline"
        label="Submission Deadline"
        rules={[
          { validator: (_, value) => {
              if (!value || !form.getFieldValue('deadline')) return Promise.resolve();
              return value.isAfter(form.getFieldValue('deadline')) ? Promise.resolve() : Promise.reject('Submission deadline must be after the challenge deadline');
            }},
        ]}
      >
        <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" />
      </Form.Item>
      <Form.Item name="prize" label="Prize (in USD)" rules={[{ required: true, message: 'Please enter the prize amount' }]}>
        <AntInput type="number" placeholder="e.g., 500.00" min={0} step="0.01" />
      </Form.Item>
      <Form.Item name="is_open" label="Status" rules={[{ required: true, message: 'Please select the status' }]}>
        <Select placeholder="Select status">
          <Option value={true}>Open</Option>
          <Option value={false}>Closed</Option>
        </Select>
      </Form.Item>
    </>
  );

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16 }}>
        <Title level={2} style={{ fontSize: '15px' }}>Manage Challenges</Title>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Input.Search
            placeholder="Search challenges by title or description"
            onSearch={handleSearch}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
            enterButton
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            Add New Challenge
          </Button>
        </div>
      </div>
      <Table
        columns={columns}
        dataSource={challenges}
        rowKey="id"
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} challenges`,
        }}
        onChange={handleTableChange}
      />

      {/* View Modal */}
      <Modal
        title="Challenge Details"
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={<Button onClick={() => setViewModalVisible(false)}>Close</Button>}
        width={600}
      >
        {selectedChallenge && (
          <div>
            <p><strong>Id:</strong> {selectedChallenge.id}</p>
            <p><strong>Title:</strong> {selectedChallenge.title}</p>
            <p><strong>Description:</strong> {selectedChallenge.description}</p>
            <p><strong>Category ID:</strong> {selectedChallenge.category_id}</p>
            <p><strong>Country:</strong> {selectedChallenge.country}</p>
            <p><strong>Organizer:</strong> {selectedChallenge.organizer || 'N/A'}</p>
            <p><strong>Difficulty Level:</strong> {selectedChallenge.difficulty_level}</p>
            <p><strong>Deadline:</strong> {moment(selectedChallenge.deadline).format('YYYY-MM-DD HH:mm:ss')}</p>
            <p><strong>Days Left:</strong> {selectedChallenge.days_left}</p>
            <p><strong>Prize:</strong> ${selectedChallenge.prize.toFixed(2)}</p>
            <p><strong>Status:</strong> {selectedChallenge.is_open ? 'Open' : 'Closed'}</p>
            <p><strong>Participants Count:</strong> {selectedChallenge.participants_count}</p>
            <p><strong>Submission Deadline:</strong> {selectedChallenge.submission_deadline ? moment(selectedChallenge.submission_deadline).format('YYYY-MM-DD HH:mm:ss') : 'N/A'}</p>
            <p><strong>Submission Count:</strong> {selectedChallenge.submission_count}</p>
            <p><strong>Created At:</strong> {moment(selectedChallenge.created_at).format('YYYY-MM-DD HH:mm:ss')}</p>
            <p><strong>Updated At:</strong> {moment(selectedChallenge.updated_at).format('YYYY-MM-DD HH:mm:ss')}</p>
          </div>
        )}
      </Modal>

      {/* Create Modal */}
      <Modal
        title="Add New Challenge"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleCreateSubmit}>
          {challengeFormFields}
          <Form.Item>
            <Button type="primary" htmlType="submit">Create Challenge</Button>
            <Button style={{ marginLeft: 8 }} onClick={() => { setCreateModalVisible(false); form.resetFields(); }}>Cancel</Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        title="Edit Challenge"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleEditSubmit}>
          {challengeFormFields}
          <Form.Item>
            <Button type="primary" htmlType="submit">Update Challenge</Button>
            <Button style={{ marginLeft: 8 }} onClick={() => { setEditModalVisible(false); form.resetFields(); }}>Cancel</Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ChallengesManage;